import React, { useEffect, useState } from 'react';
import { AddressForm, addressValidation } from '@zydalabs/storefront-components';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import useTranslation from 'next-translate/useTranslation';
import { yupResolver } from '@hookform/resolvers/yup';
import { equals, isEmpty, reject } from 'ramda';
import { Button } from '@zydalabs/zac-react';
import { StyledPageWrapper, StyledFormWrapper, StyledStickyButton } from 'common/styles';
import OpenStreetMap from 'common/components/OpenStreetMap';
import {
  useAddAddress,
  useUpdateAddress,
  useShowAddress,
  useCountryDetailsWithAreas,
  useDeleteAddress,
  useCountryDetails,
  useFetchNearestZoneTemplateCallback,
} from 'service/hooks';
import {
  usePersistedAddressArea,
  usePersistedSelectedCoordinates,
  useGlobalAddressType,
  useSelectAddressOrArea,
  usePersistedCountryDetails,
  usePersistedSupportedDeliveryZone,
  usePersistedUserAddressId,
  useIsKuwait,
} from 'common/hooks';
import { customHistory, changeRoute, logger, replaceRoute } from 'common/utils';
import { LANGUAGE_AR, URLS } from 'common/constants';
import { RenderingRegistry } from 'common/lib/registries';
import { AddressTypes } from 'common/types';
import { fetchAreaDetailsByCoordinates } from 'common/components/Paci/utils';
import ServiceConfigs from 'service/config';
import { getDefaultAddressTypeBasedOnCountry, getAddressFields, localizeErrorMessages } from '../../utils';
import AddressPageLoading from '../../components/AddressPageLoading';
import { ADDRESS_FIELDS } from '../../enums';
import { StyledDeleteButtonWrapper, StyledMapWrapper, StyledEditLocationContainer } from './styles';
import { useFulfillmentSettings } from '../../../../contexts';

const StoreFrontHeader = dynamic(() => import('common/components/StoreFrontHeader'));

const AddressPage: React.FC = () => {
  const isManualOrder = RenderingRegistry.getIsManualOrder();
  const [persistedAddressArea, , removePersistedAddressArea] = usePersistedAddressArea();
  const { area: currentArea } = useFulfillmentSettings();
  const persistedAreaRefId = isManualOrder ? currentArea?.id : persistedAddressArea?.id;
  const [persistedCountryDetails] = usePersistedCountryDetails();
  const [persistedAddressId, , removePersistedAddressId] = usePersistedUserAddressId();
  const { data: countryDetailsWithAreas } = useCountryDetailsWithAreas({ id: persistedCountryDetails?.id });
  const { data: countryDetailsDefault } = useCountryDetails({ id: persistedCountryDetails?.id });
  const [addressType, setAddressType] = useGlobalAddressType(
    getDefaultAddressTypeBasedOnCountry(persistedCountryDetails?.code),
  );

  const router = useRouter();
  const { t, lang } = useTranslation('address');
  const addressId = router?.query?.id as string;

  const [persistedSelectedCoordinates] = usePersistedSelectedCoordinates();
  const [persistedSupportedDeliveryZone] = usePersistedSupportedDeliveryZone();
  const useDeliveryzonesMs = ServiceConfigs?.getUseDeliveryzonesMs();
  const countryDetails = useDeliveryzonesMs ? countryDetailsDefault : countryDetailsWithAreas;

  // TODO: validate if we need this state or not

  const addAddress = useAddAddress({ currentAreaId: currentArea?.id });
  const updateAddress = useUpdateAddress({ addressId });
  const deleteAddress = useDeleteAddress();
  const fetchNearestZoneTemplate = useFetchNearestZoneTemplateCallback();
  const { data: userAddressDetails, isLoading: isAddressLoading } = useShowAddress({
    addressId,
  });
  const [isMutationLoading, setIsMutationLoading] = useState(false);
  const [isDzmsVerified, setIsDzmsVerified] = useState(false);
  const isLoading = isAddressLoading || isMutationLoading;
  // TODO: add use effect to set persistedAddressSelectedArea after fetching userAddressDetails

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    setError,
  } = useForm({
    context: { countryCode: countryDetails?.code },
    resolver: yupResolver(addressValidation[addressType]),
  });
  const isComingFromAddressBook = customHistory?.getPrevPath() === URLS.ADDRESS_BOOK;
  const isKuwait = useIsKuwait();
  const [isNearestZoneTemplateAvailable, setIsZoneTemplateAvailable] = useState(true);

  const onSuccess = () => {
    setIsMutationLoading(false);
    customHistory.back();
  };

  const { handleOnAddressSelected } = useSelectAddressOrArea(onSuccess);

  const addOrUpdateAddress = address => {
    if (!addressId && !isComingFromAddressBook) {
      handleOnAddressSelected({ ...address, ...(useDeliveryzonesMs && { deliveryZone: persistedSupportedDeliveryZone }) });
      removePersistedAddressArea();
    } else {
      onSuccess();
    }
  };

  const handleOnBack = () => {
    if (addressId || useDeliveryzonesMs) customHistory.back();
    else replaceRoute({ pathname: isManualOrder ? URLS.ORDER_MODE : URLS.ADDRESS_MAP });
  };

  useEffect(() => {
    const fetchAreaDetailsOnDragEnd = async () => {
      try {
        await fetchAreaDetailsByCoordinates(persistedSelectedCoordinates?.lat, persistedSelectedCoordinates?.lng, lang).then(
          (res: any) => {
            if (!isNearestZoneTemplateAvailable) {
              const area = lang === LANGUAGE_AR ? res.NeighborhoodArabic : res.NeighborhoodEnglish;
              const city = res.GovernorateArabic || 'City Not Provided'; // TODO: to be handled in form validation bec city is not visible or required for kuwait
              setValue(ADDRESS_FIELDS.AREA, area);
              setValue(ADDRESS_FIELDS.CITY, city);
              setIsDzmsVerified(!!area);
            }
            const street = lang === LANGUAGE_AR ? res.StreetArabic : res.StreetEnglish;
            const building = lang === LANGUAGE_AR ? res.TitleArabic : res.TitleEnglish;
            setValue(ADDRESS_FIELDS.STREET, street || '');
            setValue(ADDRESS_FIELDS.BUILDING, building || '');
            if (addressType === 'house' && res.HouseArabic && res.ParcelArabic) {
              setValue(ADDRESS_FIELDS.BUILDING, '');
              setValue(ADDRESS_FIELDS.HOUSE_NUMBER, `${res.HouseArabic} - ${t('parcel')} ${res.ParcelArabic}` || '');
            }
            setValue(ADDRESS_FIELDS.BLOCK, res.BlockArabic || '');
          },
        );
      } catch (error) {
        logger.error({ error: 'PACI error, auto-filling address form with area details', extraData: error });
      }
    };
    if (persistedSelectedCoordinates && isKuwait) {
      fetchAreaDetailsOnDragEnd();
    }
  }, [persistedSelectedCoordinates, isKuwait, addressType, isNearestZoneTemplateAvailable]);

  useEffect(() => {
    const lat = persistedSelectedCoordinates?.lat;
    const lng = persistedSelectedCoordinates?.lng;
    if (isAddressLoading || (addressId && !userAddressDetails) || !lat || !lng) return;
    fetchNearestZoneTemplate({ geoPoint: [lng, lat] })
      .then(nearestZoneTemplate => {
        if (nearestZoneTemplate?.nearestZoneTemplate) {
          setIsDzmsVerified(true);
          setValue(ADDRESS_FIELDS.CITY, nearestZoneTemplate.nearestZoneTemplate.cityTitleAr || '');
          setValue(ADDRESS_FIELDS.AREA, nearestZoneTemplate.nearestZoneTemplate.areaTitleAr || '');
        } else {
          setIsZoneTemplateAvailable(false);
        }
      })
      .catch(error => logger.error({ error: 'FetchNearestZoneTemplate Error', extraData: error }));
  }, [
    persistedSelectedCoordinates?.lat,
    persistedSelectedCoordinates?.lng,
    countryDetails?.code,
    userAddressDetails,
    isAddressLoading,
  ]);

  useEffect(() => {
    if (userAddressDetails) {
      setIsDzmsVerified(userAddressDetails?.dzmsVerified);
      setAddressType(userAddressDetails.unitType as AddressTypes);
      const { area, ...restData } = userAddressDetails;

      const formattedAddress = {
        ...restData,
        phoneNumber: userAddressDetails.phoneNumber || '',
        title: userAddressDetails.title || '',
        block: userAddressDetails.block || '',
        building: userAddressDetails.building || '',
        floor: userAddressDetails.floor || '',
        notes: userAddressDetails.notes || '',
        street: userAddressDetails.street || '',
        city: userAddressDetails.cityName || '',
        area: useDeliveryzonesMs ? userAddressDetails.areaName || '' : userAddressDetails?.areaId || '',
      };
      const formattedAddressWithoutNullValues = reject(equals(null))(formattedAddress);
      reset(formattedAddressWithoutNullValues);
    }
  }, [userAddressDetails, reset]);

  const handleOnSubmit = data => {
    const { id, area, areaId, covered, deliveryZone, ...restData } = data;
    const addressData = {
      ...restData,
      areaId: (persistedAreaRefId && parseInt(persistedAreaRefId)) || (areaId && parseInt(areaId)),
      areaName: data.area,
      cityName: data.city,
      city: undefined,
      area: undefined,
      unitType: addressType,
      lng: persistedSelectedCoordinates?.lng.toString(),
      lat: persistedSelectedCoordinates?.lat.toString(),
      dzmsVerified: isDzmsVerified,
    };

    // TODO: to be handled in validation in storefront-components
    if (addressData?.notes?.length > 255) {
      setError('notes', {
        type: t('notesValidation'),
      });
      return;
    }
    setIsMutationLoading(true);

    const addressMutationData = reject(isEmpty)(addressData);

    if (addressId) {
      updateAddress(addressMutationData)
        .then(addOrUpdateAddress)
        .catch(
          ({ errors: err }) =>
            err &&
            setError(err.attribute, {
              type: 'validation',
              message: err.message,
            }),
        );
    } else {
      addAddress(addressMutationData)
        .then(addOrUpdateAddress)
        .catch(
          ({ errors: err }) =>
            err &&
            setError(err.attribute, {
              type: 'validation',
              message: err.message,
            }),
        );
    }
  };

  const handleOnDelete = async Id => {
    setIsMutationLoading(true);
    const isSuccess = await deleteAddress({ addressId: Id });
    setIsMutationLoading(false);

    if (isSuccess) {
      if (persistedAddressId === Id) {
        removePersistedAddressId();
      }
      onSuccess();
    }
  };

  if (isAddressLoading) return <AddressPageLoading />;

  const addressFields = getAddressFields(t, countryDetails?.code);

  return (
    <StyledPageWrapper>
      <StoreFrontHeader title={t('address')} onBack={handleOnBack} />
      {useDeliveryzonesMs && (
        <StyledEditLocationContainer>
          <StyledMapWrapper>
            <OpenStreetMap
              center={persistedSelectedCoordinates}
              zoom={15}
              hasAutoComplete={false}
              hasCurrLocationButton={false}
              isStatic
            />
          </StyledMapWrapper>
          <Button
            type="button"
            onClick={() =>
              changeRoute({
                pathname: URLS.ORDER_DELIVERY,
                query: { ...router.query, zoom: 16 },
              })
            }
            size="medium"
            testId="edit_location_btn"
            variant="tertiary"
            text={t('editLocation')}
          />
        </StyledEditLocationContainer>
      )}
      <StyledFormWrapper>
        <AddressForm
          fields={addressFields[addressType]}
          errors={localizeErrorMessages(addressFields[addressType], errors, t)}
          control={control}
          autoFocus
          addressType={addressType}
          setAddressType={setAddressType}
          t={t}
          countryCode={countryDetails?.code}
          dzmsVerified={isDzmsVerified}
        />
      </StyledFormWrapper>
      {!!addressId && (
        <StyledDeleteButtonWrapper>
          <Button
            type="button"
            variant="tertiary"
            isFluid
            onClick={() => handleOnDelete(addressId)}
            isDisabled={isLoading}
            isLoading={isLoading}
            text={t('deleteAddress')}
            testId="save_address_btn"
          />
        </StyledDeleteButtonWrapper>
      )}
      <StyledStickyButton>
        <Button
          type="submit"
          isFluid
          onClick={handleSubmit(handleOnSubmit)}
          isDisabled={isLoading}
          isLoading={isLoading}
          text={addressId ? t('updateAddress') : t('addAddress')}
          testId="save_address_btn"
        />
      </StyledStickyButton>
    </StyledPageWrapper>
  );
};

export default AddressPage;
